<script>
import { mapGetters } from 'vuex';
import router from '../dashboard/routes';
import AddAccountModal from '../dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpgradeBanner from './components/app/UpgradeBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { setDatadogRumUser } from '../helper/datadogRum';
import {
  setAmplitudeUser,
  setAmplitudeUserProperties,
  setAmplitudeAccount,
} from '../shared/amplitude';
import CloudchatSupportWidget from 'dashboard/components/CloudchatSupportWidget.vue';

export default {
  name: 'App',

  components: {
    AddAccountModal,
    CloudchatSupportWidget,
    LoadingState,
    NetworkNotification,
    PaymentPendingBanner,
    WootSnackbarBox,
    UpgradeBanner,
    PendingEmailVerificationBanner,
  },
  data() {
    return {
      showAddAccountModal: false,
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      currentAccountId: 'getCurrentAccountId',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentUser() {
      if (!this.hasAccounts) {
        this.showAddAccountModal = true;
      }
    },
    currentAccountId() {
      if (this.currentAccountId) {
        this.initializeAccount();
      }
    },
    // The immediate fire inside startCognitoTokenRefresh() runs before the
    // store has resolved currentUser via validityCheck (currentUser starts
    // as `{ id: null }`), so maybeRefreshCognitoToken bails on the id guard
    // and the next attempt is 10 minutes away. Watching the id lets us
    // trigger the first refresh as soon as the user is known, closing the
    // page-load → near-expiry token → 401 gap.
    'currentUser.id'(newId, oldId) {
      if (newId && !oldId) {
        this.maybeRefreshCognitoToken();
      }
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    this.setLocale(window.chatwootConfig.selectedLocale);
    this.startCognitoTokenRefresh();
  },
  beforeDestroy() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    this.stopCognitoTokenRefresh();
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    startCognitoTokenRefresh() {
      // Poll cadence picked to comfortably beat the backend's 15-minute
      // refresh-on-near-expiry threshold while staying well under the
      // Cognito id_token 24h TTL — gives the SPA at least one refresh
      // attempt before any in-flight Claudia microfrontend request can
      // hit a real Cognito expiry and trigger an involuntary logout.
      const COGNITO_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
      // 1s debounce on visibilitychange: rapid Cmd+Tab cycles fire the
      // event multiple times in close succession; without a debounce
      // each one triggers a /auth/cognito/id_token round-trip.
      const VISIBILITY_DEBOUNCE_MS = 1000;
      // Fire once immediately. A tab can mount with a token already inside
      // the near-expiry window (cross-instance handoff, sessions that
      // survived a deploy, etc) — without this the first refresh attempt
      // is 10 minutes away, which is past the threshold in those cases.
      this.maybeRefreshCognitoToken();
      // Store timer/handler refs as plain instance props (not in data()) so
      // Vue 2 doesn't install getter/setter on them. Reactivity here has
      // no consumer — the values are only ever cleared on teardown.
      this.$_cognitoTokenIntervalId = setInterval(
        () => this.maybeRefreshCognitoToken(),
        COGNITO_REFRESH_INTERVAL_MS
      );
      this.$_cognitoTokenVisibilityHandler = () => {
        if (document.visibilityState !== 'visible') return;
        if (this.$_cognitoVisibilityDebounce) {
          clearTimeout(this.$_cognitoVisibilityDebounce);
        }
        this.$_cognitoVisibilityDebounce = setTimeout(
          () => this.maybeRefreshCognitoToken(),
          VISIBILITY_DEBOUNCE_MS
        );
      };
      document.addEventListener(
        'visibilitychange',
        this.$_cognitoTokenVisibilityHandler
      );
    },
    stopCognitoTokenRefresh() {
      if (this.$_cognitoTokenIntervalId) {
        clearInterval(this.$_cognitoTokenIntervalId);
        this.$_cognitoTokenIntervalId = null;
      }
      if (this.$_cognitoVisibilityDebounce) {
        clearTimeout(this.$_cognitoVisibilityDebounce);
        this.$_cognitoVisibilityDebounce = null;
      }
      if (this.$_cognitoTokenVisibilityHandler) {
        document.removeEventListener(
          'visibilitychange',
          this.$_cognitoTokenVisibilityHandler
        );
        this.$_cognitoTokenVisibilityHandler = null;
      }
    },
    maybeRefreshCognitoToken() {
      if (!this.currentUser?.id) return;
      // Skip users that did not log in via Cognito (email/password fallback,
      // local-only sessions). Without this guard every poll hits the backend
      // just to receive `{ id_token: null }` and get discarded.
      if (!this.currentUser?.cognito_access_token) return;
      this.$store.dispatch('refreshCognitoAccessToken');
    },
    async initializeAccount() {
      setAmplitudeUser(this.currentUser.email);
      // Multi-instance note: emails are globally unique across Cloud Chat
      // instances (instance2, instance3, default), so setAmplitudeUser(email)
      // already gives us a stable cross-instance identity. The `instance` user
      // property is for slicing Amplitude charts ("adoption only on
      // instance3"), not disambiguation.
      setDatadogRumUser(this.currentUser);
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      // Read from the store AFTER `accounts/get` so the active account is
      // fresh — `currentUser.accounts` from the auth payload may be stale or
      // empty for tech / tenant-impersonation logins (which have no direct
      // membership). getAccount(currentAccountId) is keyed on the route, so
      // multi-account agents always see the account they're actually viewing.
      const account = this.getAccount(this.currentAccountId);
      const { locale, latest_chatwoot_version: latestChatwootVersion } = account;
      setAmplitudeAccount({ id: this.currentAccountId, name: account.name });
      // `instance` lives separate from account dimensions so it can be sliced
      // independently in Amplitude (e.g. "instance3 adoption" across all
      // accounts). Falls back to 'unknown' so the property is always present
      // — null/undefined would be dropped by setAmplitudeUserProperties.
      setAmplitudeUserProperties({
        instance:
          globalThis.chatwootConfig?.cloudchat_api_instance_id || 'unknown',
      });
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      this.setLocale(locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(pubsubToken);
      this.$store.dispatch('notifications/unReadCount');
      this.reconnectService = new ReconnectService(this.$store, router);

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex-grow-0 w-full h-full min-h-0 app-wrapper"
    :class="{ 'app-rtl--wrapper': isRTL }"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
      <UpgradeBanner />
    </template>
    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
    <AddAccountModal :show="showAddAccountModal" :has-accounts="hasAccounts" />
    <WootSnackbarBox />
    <NetworkNotification />
    <CloudchatSupportWidget />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import 'vue-easytable/libs/theme-default/index.css';
@import './assets/scss/app';
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
